import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronLeft, FileText, Plus, Trash2, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { apiCall } from '../../services/apiUtil';
import toast from 'react-hot-toast';
import { generateDocumentHtml } from '../utils/documentHtml';

const STEP_SELECT = 1;
const STEP_PREVIEW = 2;

// Used by tournaments and by academy programs. The two owners differ only in
// which endpoints back the template and the generated document, so those are
// injected rather than branched on here.
export default function TermsBuilderModal({
  type, tournamentId, tournamentName, onSaved, onClose,
  entityName,
  ownerLabel = 'Tournament',
  templatePath = '/tournaments/templates',
  generatePath,
}) {
  const docOwnerName = entityName ?? tournamentName ?? '';
  const generateEndpoint = generatePath ?? `/tournaments/${tournamentId}/documents/generate`;
  const { theme } = useTheme();
  const [step, setStep] = useState(STEP_SELECT);
  const [templateSections, setTemplateSections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [customClauses, setCustomClauses] = useState([]);
  const [newClause, setNewClause] = useState({ title: '', content: '' });
  const [showAddClause, setShowAddClause] = useState(false);
  const [expandedClauses, setExpandedClauses] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedClauses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const title = type === 'terms' ? 'Terms & Conditions' : 'Liability Waiver';

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Load template
  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiCall(`${templatePath}/${type}`);
        // Normalise: support both `id` and `key` fields from S3 templates
        const sections = (data.sections || []).map(s => ({ ...s, id: s.id || s.key }));
        setTemplateSections(sections);
        setSelected(new Set(sections.map(s => s.id)));
      } catch {
        toast.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type, templatePath]);

  const optionalIds = templateSections.filter(s => !s.required).map(s => s.id);
  const allOptionalSelected = optionalIds.length > 0 && optionalIds.every(id => selected.has(id));

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOptionalSelected) {
        optionalIds.forEach(id => next.delete(id));
      } else {
        optionalIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleSection = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addCustomClause = () => {
    if (!newClause.title.trim() || !newClause.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }
    setCustomClauses(prev => [...prev, { ...newClause, id: `custom-${Date.now()}` }]);
    setNewClause({ title: '', content: '' });
    setShowAddClause(false);
  };

  const removeCustomClause = (id) => {
    setCustomClauses(prev => prev.filter(c => c.id !== id));
  };

  const buildPreview = () => {
    const chosenSections = templateSections.filter(s => selected.has(s.id));
    const allSections = [...chosenSections, ...customClauses];
    setPreviewHtml(generateDocumentHtml({
      type,
      tournamentName: docOwnerName,
      ownerLabel,
      createdAt: new Date().toISOString(),
      sections: allSections,
    }));
    setStep(STEP_PREVIEW);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const chosenSections = templateSections.filter(s => selected.has(s.id));
      const data = await apiCall(generateEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          type,
          sections: chosenSections,
          customClauses,
          tournamentName: docOwnerName,
          programName: docOwnerName,
        }),
      });
      toast.success(`${title} created successfully`);
      onSaved(data.path ?? data.key);
      onClose();
    } catch {
      toast.error(`Failed to save ${title}`);
    } finally {
      setSaving(false);
    }
  };

  const isDark = theme.background !== 'bg-white';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme.border}`}>
          <div className="flex items-center gap-x-3">
            {step === STEP_PREVIEW && (
              <button onClick={() => setStep(STEP_SELECT)} className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${theme.textMuted}`}>
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <FileText className="w-5 h-5 text-blue-500" />
            <div>
              <h2 className={`font-semibold text-base ${theme.text}`}>Create {title}</h2>
              <p className={`text-xs ${theme.textMuted}`}>
                {step === STEP_SELECT ? 'Select clauses to include' : 'Preview your document'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${theme.textMuted}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex px-5 pt-3 gap-x-2">
          {[{ n: 1, label: 'Select Clauses' }, { n: 2, label: 'Preview' }].map(s => (
            <div key={s.n} className="flex items-center gap-x-1.5">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= s.n ? 'bg-blue-600 text-white' : `${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`
              }`}>{s.n}</div>
              <span className={`text-xs ${step >= s.n ? 'text-blue-500 font-medium' : theme.textMuted}`}>{s.label}</span>
              {s.n < 2 && <div className={`w-8 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : step === STEP_SELECT ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className={`text-sm ${theme.textSecondary}`}>
                  Required clauses are pre-selected and cannot be removed. Add or remove optional clauses as needed.
                </p>
                {optionalIds.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-xs text-blue-500 hover:text-blue-600 font-medium flex-shrink-0 ml-3"
                  >
                    {allOptionalSelected ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {templateSections.map(section => (
                <div
                  key={section.id}
                  className={`flex gap-x-3 p-3 rounded-lg border transition-colors ${
                    selected.has(section.id)
                      ? `border-blue-400 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`
                      : `${theme.border} ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(section.id)}
                    onChange={() => !section.required && toggleSection(section.id)}
                    disabled={section.required}
                    className={`w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0 ${section.required ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={`flex items-center gap-x-2 ${section.required ? '' : 'cursor-pointer'}`}
                      onClick={() => !section.required && toggleSection(section.id)}
                    >
                      <span className={`text-sm font-medium ${theme.text}`}>{section.title}</span>
                      {section.required && (
                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded">Required</span>
                      )}
                    </div>
                    <p className={`text-xs ${theme.textMuted} mt-1 ${expandedClauses.has(section.id) ? '' : 'line-clamp-2'}`}>
                      {section.content}
                    </p>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(section.id)}
                      className="text-xs text-blue-500 hover:text-blue-600 mt-0.5 font-medium"
                    >
                      {expandedClauses.has(section.id) ? 'Less' : 'More'}
                    </button>
                  </div>
                </div>
              ))}

              {/* Custom clauses */}
              {customClauses.map(clause => (
                <div key={clause.id} className={`flex gap-x-3 p-3 rounded-lg border border-green-400 ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${theme.text}`}>{clause.title}</span>
                      <button type="button" onClick={() => removeCustomClause(clause.id)} className="text-red-400 hover:text-red-600 p-0.5">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className={`text-xs ${theme.textMuted} mt-1 ${expandedClauses.has(clause.id) ? '' : 'line-clamp-2'}`}>
                      {clause.content}
                    </p>
                    <button
                      type="button"
                      onClick={e => { e.preventDefault(); toggleExpanded(clause.id); }}
                      className="text-xs text-blue-500 hover:text-blue-600 mt-0.5 font-medium"
                    >
                      {expandedClauses.has(clause.id) ? 'Less' : 'More'}
                    </button>
                  </div>
                </div>
              ))}

              {/* Add custom clause */}
              {showAddClause ? (
                <div className={`p-3 rounded-lg border ${theme.border} space-y-2`}>
                  <input
                    type="text"
                    placeholder="Clause title"
                    value={newClause.title}
                    onChange={e => setNewClause(p => ({ ...p, title: e.target.value }))}
                    className={`w-full px-3 py-2 text-sm border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  <textarea
                    placeholder="Clause content..."
                    value={newClause.content}
                    onChange={e => setNewClause(p => ({ ...p, content: e.target.value }))}
                    rows={3}
                    className={`w-full px-3 py-2 text-sm border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                  />
                  <div className="flex justify-end gap-x-2">
                    <button type="button" onClick={() => { setShowAddClause(false); setNewClause({ title: '', content: '' }); }}
                      className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}>
                      Cancel
                    </button>
                    <button type="button" onClick={addCustomClause}
                      className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}>
                      Add Clause
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAddClause(true)}
                  className={`w-full flex items-center justify-center gap-x-2 p-3 rounded-lg border-2 border-dashed ${theme.border} ${theme.textMuted} hover:border-blue-400 hover:text-blue-500 transition-colors text-sm`}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Custom Clause</span>
                </button>
              )}
            </div>
          ) : (
            /* Step 2: Preview */
            <div className="rounded-lg overflow-hidden border border-gray-200">
              <iframe
                srcDoc={previewHtml}
                title={`${title} Preview`}
                className="w-full"
                style={{ height: '420px', border: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-5 py-4 border-t ${theme.border}`}>
          <span className={`text-xs ${theme.textMuted}`}>
            {step === STEP_SELECT
              ? `${selected.size} clause${selected.size !== 1 ? 's' : ''} selected${customClauses.length > 0 ? ` + ${customClauses.length} custom` : ''}`
              : 'Review your document before creating'}
          </span>
          <div className="flex gap-x-2">
            <button type="button" onClick={onClose} className={`${btn.base} ${btn.md} ${theme.buttonGhost}`}>
              Cancel
            </button>
            {step === STEP_SELECT ? (
              <button
                type="button"
                onClick={buildPreview}
                disabled={selected.size === 0}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
              >
                Preview →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-2`}
              >
                {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                <span>{saving ? 'Creating...' : 'Create Document'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
